/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, use, useCallback, useEffect, useMemo, useRef, useTransition } from 'react';
import {
    NavigationType,
    useAsyncError,
    useLocation,
    useNavigation,
    useNavigationType,
    useRouteLoaderData,
} from 'react-router';
import type { loader as rootLoader } from '@/root';
import type { Route } from './+types/_app.category.$categoryId';
import type { ShopperProducts, ShopperSearch } from '@/scapi';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';
import { fetchCategory } from '@/lib/api/categories.server';
import { fetchSearchProducts } from '@/lib/api/search.server';
import { getAllQueryParams, getQueryParam, PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';
import { getConfig, useConfig } from '@salesforce/storefront-next-runtime/config';
import { siteContext } from '@salesforce/storefront-next-runtime/site-context';
import CategoryBreadcrumbs from '@/components/category-breadcrumbs';
import CategoryPagination from '@/components/category-pagination';
import LoadMore from '@/components/product-grid/load-more';
import { useLoadMoreProducts } from '@/hooks/use-load-more-products';
import ActiveFilters from '@/components/category-refinements/active-filters';
import FiltersButton from '@/components/category-refinements/filters-button';
import CategoryRefinements from '@/components/category-refinements';
import CategorySorting from '@/components/category-sorting';
import DeferredProductGrid from '@/components/product-grid';
import { ProductTileSkeleton, CategoryGridSkeleton } from '@/components/category-skeleton';
import QuickFilters from '@/components/quick-filters';
import { useAnalytics } from '@/hooks/use-analytics';
import { PageType } from '@/lib/decorators/page-type';

import { JsonLd } from '@/components/json-ld';
import { SeoMeta } from '@/components/seo-meta';
import { useTranslation } from 'react-i18next';
import { UITarget } from '@/targets/ui-target';
import { generateCategorySchema } from '@/utils/category-schema';
import { getPublicOrigin } from '@/utils/schema-url';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import {
    getInitialFiltersOpen,
    getSearchWithoutClientOnlyParams,
    getSearchWithoutFiltersParam,
    useFiltersPanelState,
} from '@/hooks/use-filters-panel-state';
import { getLogger } from '@/lib/logger.server';
import { uiConfig } from '@/lib/config.ui';
import { Region } from '@/components/region';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { isDesignModeActive, isPreviewModeActive } from '@salesforce/storefront-next-runtime/design/mode';
// RGBARRY-CUSTOMIZATION: instant-URL nav — defer heavy PLP data on client navigations only, bridge with a PLP skeleton
import { isClientNavigationDataRequest } from '@/lib/routes/request-mode.server';

@PageType({
    name: 'Product Listing Page',
    description: 'Product listing page with product listings and personalized content',
    supportedAspectTypes: ['plp'],
})
@RegionDefinition([
    {
        id: 'plpTopFullWidth',
        name: 'Top Full Width Region',
        description: 'Full screen width region at the top of the results, below the header and above the breadcrumb',
        maxComponents: 5,
    },
])
export class ProductListingPageMetadata {}

type CategoryPageData = {
    category: ShopperProducts.schemas['Category'];
    // RGBARRY-CUSTOMIZATION: instant-URL — resolved object on document/SSR requests (present inline in
    // the first-paint HTML for SEO/LCP); an unresolved Promise on client navigations (deferred so the
    // loader returns and React Router commits the URL on click, then streamed and consumed via use()).
    searchResultCritical:
        | ShopperSearch.schemas['ProductSearchResult']
        | Promise<ShopperSearch.schemas['ProductSearchResult']>;
    searchResultNonCritical: Promise<ShopperSearch.schemas['ProductSearchResult']>;
    categoryId: string;
    pageUrl: string;
    refine: string[];
    currency: string;
    locale: string;
    initialFiltersOpen?: boolean;
    categorySchema: Promise<ReturnType<typeof generateCategorySchema> | null>;
    /** `rel=prev/next` crawl URLs for load-more mode, or `null` in traditional mode. Always `null` on
     *  client navigations (crawlers only ever issue document requests). */
    seoPagination: { prevUrl: string | null; nextUrl: string | null } | null;
    /** Server-side offset for `?page=N` entry in load-more mode (0 for normal entry). */
    offset?: number;
    /** RGBARRY-CUSTOMIZATION: PD plpTopFullWidth zone. Awaited in the loader (blocks the grid) so
     *  it never pops in after first paint — but raced against a short timeout guard so a
     *  slow/unavailable PD fetch degrades to empty instead of holding up the whole PLP past the
     *  entry.server.tsx stream abort (see removal history in commit 1a6b7a9).
     *  RGBARRY-CUSTOMIZATION: instant-URL — resolved on document/SSR requests; a Promise on client
     *  navigations (deferred alongside the critical search, consumed via use()). */
    page?:
        | Awaited<ReturnType<typeof fetchPageWithComponentData>>
        | Promise<Awaited<ReturnType<typeof fetchPageWithComponentData>>>;
};

// RGBARRY-CUSTOMIZATION: brand PLP renders on a white surface, pulled up under the header.
// RGBARRY-CUSTOMIZATION: `analytics.routeType` classifies this route for the GTM data layer's
// page-view event (see src/analytics/route-analytics.ts). Route-based, so it survives the legacy
// bare-slug PLP URL shape that no path-prefix blocklist could match.
export const handle = {
    ui: { main: { backgroundClass: 'bg-white' } },
    analytics: { routeType: 'category' },
};

/**
 * Server-side loader function that fetches category data and product search results.
 * This function runs on the server during SSR and prepares data for the category page.
 * @returns Object containing search results, category data, and page metadata
 */
export async function loader(args: Route.LoaderArgs): Promise<CategoryPageData> {
    const {
        context,
        request,
        params: { categoryId },
    } = args;
    const requestUrl = new URL(request.url);
    const { searchParams } = requestUrl;
    const logger = getLogger(context);
    logger.debug('Category: loader starting', {
        categoryId,
        offset: parseInt(searchParams.get('offset') || '0', 10),
    });
    const sort = getQueryParam(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.SORT);
    const refine = getAllQueryParams(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.REFINE);
    const initialFiltersOpen = getInitialFiltersOpen(searchParams);

    // Get currency and locale for cache-busting the page key
    const config = getConfig(context);
    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        logger.error('Category: site context is not available');
        throw new Response('Site context is not available', { status: 500 });
    }
    const { currency } = siteCtx;
    const locale = siteCtx.locale.id;
    const limit = config.search.products.hits.limit;

    // Pagination params. Which ones are honored depends on the merchant's pagination mode:
    // - `traditional`: `?offset=N` drives the numbered page window (existing behavior).
    // - `load-more`: the client persists loaded count to sessionStorage for back-nav restoration;
    //   `?page=N` is the crawler/no-JS fallback that server-renders a single traditional page window.
    const paginationConfig = uiConfig.pages.category.pagination;
    const isLoadMoreMode = paginationConfig.mode === 'load-more';
    const requestedOffset = parseInt(getQueryParam(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.OFFSET) || '0', 10) || 0;
    const requestedPage = parseInt(searchParams.get('page') || '0', 10) || 0;
    // In load-more mode a `?page=N` request is the SEO/no-JS path — serve that page's offset window.
    const isPagedRequest = isLoadMoreMode && requestedPage > 1;
    const offset = isPagedRequest ? (requestedPage - 1) * limit : requestedOffset;
    const initialFetchLimit = limit;

    // Keep non-category refinements and apply exactly one category refinement.
    // If URL already contains a cgid refine (e.g. from quick filters), honor it.
    // Otherwise, default to the category id from the route path.
    const effectiveRefine = refine.filter((r) => !r.startsWith('cgid='));
    const selectedCgidRefine = refine.find((r) => r.startsWith('cgid='));
    effectiveRefine.push(selectedCgidRefine ?? `cgid=${categoryId}`);

    // Ensure criticalCount doesn't exceed limit to prevent negative non-critical limit
    const criticalCount = config.search.products.hits.critical ?? 4;
    const safeCriticalCount = Math.min(criticalCount, limit);
    // RGBARRY-CUSTOMIZATION: instant-URL nav — defer the heavy PLP data on client-side navigations only.
    // On a document/SSR request (or any request missing Sec-Fetch-Dest) we keep awaiting critical data
    // inline so products land in the first-paint HTML for SEO/LCP. On an in-app nav (Sec-Fetch-Dest:
    // empty) we return the critical search as an UNRESOLVED promise so the loader returns immediately —
    // React Router then commits the new URL on click while <CategoryPage>'s Suspense shows the nav logo,
    // and the deferred result streams in and is consumed via use().
    const deferCritical = isClientNavigationDataRequest(request);
    // Standalone empty-result literal used when the critical search faults (kept separate from the
    // resolved value so it is valid whether or not critical has been awaited).
    const emptyCriticalResult = {
        limit: safeCriticalCount,
        hits: [],
        query: '',
        refinements: [],
        sortingOptions: [],
        searchPhraseSuggestions: {},
        offset,
        total: 0,
    } as unknown as ShopperSearch.schemas['ProductSearchResult'];

    // RGBARRY-CUSTOMIZATION: maximize PLP loader parallelism — category validation and the independent
    // critical product search start in the same turn. The category remains awaited for status/SEO, while
    // the non-critical search below still waits for the actual critical hit count to preserve gap-free offsets.
    const categoryPromise = fetchCategory(context, categoryId, 1).catch((e): never => {
        if (e instanceof NormalizedApiError && e.status) {
            throw new Response(e.message, { status: e.status });
        }
        throw new Response('Internal Server Error', { status: 500 });
    });

    // RGBARRY-CUSTOMIZATION: guard the critical search so a SCAPI ProductSearch fault (e.g. the platform
    // "could not be processed within the timeout threshold" on an uncached heavy query) never takes down
    // the whole PLP via the route error boundary. Degrade to an empty result so the page shell,
    // breadcrumbs and refinements still render; the non-critical batch still streams below. Never
    // rejects, so it is safe both to await (document) and to defer/stream (client nav).
    const criticalSearchPromise: Promise<ShopperSearch.schemas['ProductSearchResult']> = fetchSearchProducts(context, {
        limit: safeCriticalCount,
        offset,
        sort,
        refine: effectiveRefine,
        currency,
    }).catch((e) => {
        getLogger(context).error('Category PLP critical product search failed; rendering empty result', {
            categoryId,
            error: e instanceof Error ? e.message : String(e),
        });
        return emptyCriticalResult;
    });

    const pageDesignerModeActive = isDesignModeActive(request) || isPreviewModeActive(request);
    let pdTimedOut = false;
    let pdFetchStart = 0;
    const startPageDesignerFetch = () => {
        pdFetchStart = performance.now();
        let pdTimeoutId: ReturnType<typeof setTimeout> | undefined;
        return Promise.race([
            fetchPageWithComponentData(args, { aspectType: 'plp', categoryId })
                .catch((e): null => {
                    getLogger(context).error('Category PLP Page Designer fetch failed; rendering PD zones empty', {
                        categoryId,
                        error: e instanceof Error ? e.message : String(e),
                    });
                    return null;
                })
                .finally(() => {
                    if (pdTimeoutId !== undefined) clearTimeout(pdTimeoutId);
                }),
            new Promise<null>((resolve) => {
                pdTimeoutId = setTimeout(() => {
                    pdTimedOut = true;
                    resolve(null);
                }, 800);
            }),
        ]);
    };
    // RGBARRY-CUSTOMIZATION: Page Designer edit/preview targets are URL-driven and do not need the
    // category hook flag, so start them immediately alongside category and search.
    const eagerPdPagePromise = pageDesignerModeActive ? startPageDesignerFetch() : undefined;

    const categoryData = await categoryPromise;

    // RGBARRY-CUSTOMIZATION: only fetch the plpTopFullWidth Page Designer content when the category
    // response hook reports an assigned PLP. The hook performs PageMgr.getPageByCategory inside the
    // category request this loader already needs and stamps `c_hasPageDesignerPage`, avoiding a
    // separate SCAPI getPages round-trip for the common no-content case. This computed response field
    // is intentionally distinct from the original persisted `c_hasContent` Category attribute.
    // Edit/preview mode deliberately bypasses the gate because Page Designer can target a draft or
    // not-yet-assigned page via pageId/pdToken.
    //
    // In normal storefront mode this starts AFTER fetchCategory (we need the flag), while edit/preview
    // starts it eagerly above because its URL-driven target is independent. The PD latency overlaps the
    // critical search, and the zone renders together with the grid — not popping in after first paint,
    // which is a real CLS hit (see removal history in commit 1a6b7a9 for why this fetch must never hang
    // the whole route). The request is raced against a short 800ms timeout guard — much shorter than the
    // old deferred-era 9s ceiling, since it blocks the entire PLP grid — so a slow/unavailable PD fetch
    // degrades to empty instead of holding the PLP past the entry.server.tsx stream abort; tune once the
    // MRT Data Store hit rate is known from the timing log below. When unflagged, resolve to null
    // immediately: zero PD round-trip.
    const shouldFetchPageDesigner = categoryData.c_hasPageDesignerPage === true || pageDesignerModeActive;
    const pdPagePromise =
        eagerPdPagePromise ?? (shouldFetchPageDesigner ? startPageDesignerFetch() : Promise.resolve(null));
    // On document requests resolve now (products inline in the SSR HTML) and keep the value for the SEO
    // derivations below; on client navs hand the component the unresolved promise (consumed via use()).
    const resolvedCritical = deferCritical ? undefined : await criticalSearchPromise;
    const searchResultCritical: CategoryPageData['searchResultCritical'] = deferCritical
        ? criticalSearchPromise
        : (resolvedCritical as ShopperSearch.schemas['ProductSearchResult']);
    // RGBARRY-CUSTOMIZATION: the non-critical batch is a DEFERRED promise returned in loader data and
    // streamed by React Router. It must NEVER reject — a rejection here escapes as a server-side
    // unhandled rejection during SSR streaming and crashes the whole PLP via the root error boundary
    // ("Something went wrong / TypeError: network error"), even though the product grid has its own
    // errorElement (that boundary only catches the *derived* promise the grid consumes, not this raw
    // one). The prior Promise.race only guarded SLOW responses (9s timeout); a FAST rejection — which is
    // exactly what a cold, expensive shop-all query (New-Arrivals) produces when the SCAPI search faults
    // or the platform trips its internal timeout — settled the race with a rejection before the timeout
    // could fire. Wrapping the fetch in `.catch()` (resolve-to-empty, same shape as the timeout branch)
    // closes that hole: the page shell, breadcrumbs, refinements and critical hits still render, and the
    // grid simply shows no additional rows instead of the whole route crashing.
    const emptyNonCriticalResult = { ...emptyCriticalResult };
    let nonCriticalTimeoutId: ReturnType<typeof setTimeout>;
    const searchResultNonCritical = Promise.race([
        // RGBARRY-CUSTOMIZATION: instant-URL — chain the non-critical window off `criticalSearchPromise`
        // (never rejects) rather than the loader body's awaited critical value, so it works identically
        // whether critical is awaited (document) or deferred (client nav). The window still bases on the
        // critical result's ACTUAL hit count (effectiveCriticalCount), preserving the gap-prevention
        // behavior — non-critical picks up exactly where critical left off.
        criticalSearchPromise
            .then((critical) => {
                const effectiveCriticalCount = critical.hits?.length ?? 0;
                return fetchSearchProducts(context, {
                    // On a `?loaded=N` restoration this widens to N so the full run is server-rendered;
                    // otherwise it's the normal single-page window.
                    limit: initialFetchLimit - effectiveCriticalCount,
                    offset: offset + effectiveCriticalCount,
                    sort,
                    refine: effectiveRefine,
                    currency,
                });
            })
            .catch((e): ShopperSearch.schemas['ProductSearchResult'] => {
                getLogger(context).error('Category PLP non-critical product search failed; streaming empty result', {
                    categoryId,
                    error: e instanceof Error ? e.message : String(e),
                });
                return emptyNonCriticalResult;
            })
            .finally(() => clearTimeout(nonCriticalTimeoutId)),
        new Promise<ShopperSearch.schemas['ProductSearchResult']>((resolve) => {
            // RGBARRY-CUSTOMIZATION: 9s deadline (was 4s) sits just under the 11s stream abort, giving the
            // non-critical batch room to arrive on slow sandbox latency instead of degrading to empty hits.
            nonCriticalTimeoutId = setTimeout(() => resolve(emptyNonCriticalResult), 9_000);
        }),
    ]);

    const pageUrl = buildCanonicalUrl(requestUrl.origin, requestUrl.pathname, requestUrl.search);

    // RGBARRY-CUSTOMIZATION: instant-URL — await the PD zone inline on document/SSR requests (renders
    // with the grid, no CLS pop-in) but defer it on client navs (so it never re-adds latency to the
    // loader return / URL commit). Timing is logged once the fetch settles on either path. When the
    // category hook reports no assigned page, pdPagePromise resolved to null synchronously — no fetch was
    // issued (skipped: true), so durationMs is reported as 0.
    const logPdTiming = (resolved: Awaited<typeof pdPagePromise>) =>
        getLogger(context).debug('Category PLP Page Designer fetch timing', {
            categoryId,
            skipped: !shouldFetchPageDesigner,
            durationMs: shouldFetchPageDesigner ? Math.round(performance.now() - pdFetchStart) : 0,
            timedOut: pdTimedOut,
            hasContent: !!resolved,
        });
    let page: CategoryPageData['page'];
    if (deferCritical) {
        page = pdPagePromise;
        void pdPagePromise.then(logPdTiming);
    } else {
        const resolvedPage = await pdPagePromise;
        logPdTiming(resolvedPage);
        page = resolvedPage;
    }

    // SEO / crawler pagination (load-more mode only). Expose `rel=prev/next` URLs so bots can discover
    // and crawl the full result set via `?page=N` even though shoppers use the JS "load more" flow.
    // These use the clean path + a single `page` param (canonical stays the base URL; see SeoMeta).
    // RGBARRY-CUSTOMIZATION: instant-URL — SEO `rel=prev/next` hints derive from the resolved critical
    // total, which we only have inline on document requests. Client navs never come from crawlers, so
    // there is nothing to lose by emitting `null` there (and we avoid awaiting critical to build it).
    const totalCount = resolvedCritical?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / limit));
    const currentPage = isPagedRequest ? requestedPage : 1;
    const buildPageUrl = (p: number): string => {
        const params = new URLSearchParams();
        for (const r of refine) params.append('refine', r);
        if (sort) params.set('sort', sort);
        if (p > 1) params.set('page', String(p));
        const qs = params.toString();
        return `${requestUrl.origin}${requestUrl.pathname}${qs ? `?${qs}` : ''}`;
    };
    const seoPagination =
        !deferCritical && isLoadMoreMode
            ? {
                  prevUrl: currentPage > 1 ? buildPageUrl(currentPage - 1) : null,
                  nextUrl: currentPage < totalPages ? buildPageUrl(currentPage + 1) : null,
              }
            : null;

    // Generate category schema in loader (server-side) for SEO.
    // RGBARRY-CUSTOMIZATION: instant-URL — compose against `criticalSearchPromise` (never-rejects)
    // rather than reading `searchResultCritical.hits` synchronously, since critical may be an
    // unresolved promise on client navigations. Composed in the loader (stable identity), not render.
    const categorySchemaPromise = Promise.all([criticalSearchPromise, searchResultNonCritical])
        .then(
            ([critical, searchResult]: [
                ShopperSearch.schemas['ProductSearchResult'],
                ShopperSearch.schemas['ProductSearchResult'],
            ]) => {
                try {
                    // Use public origin from request headers instead of request.url
                    // to avoid exposing internal AWS Lambda URLs in schema
                    const publicOrigin = getPublicOrigin(request);
                    const url = new URL(request.url);
                    const schemaPageUrl = `${publicOrigin}${url.pathname}${url.search}`;
                    // Validate inputs before generating schema
                    if (!categoryData || !searchResult) {
                        return null;
                    }
                    return generateCategorySchema({
                        category: categoryData,
                        searchResult: {
                            ...searchResult,
                            hits: [...(critical.hits || []), ...(searchResult.hits || [])],
                        },
                        config,
                        pageUrl: schemaPageUrl,
                        defaultCurrency: currency,
                    });
                } catch (error) {
                    logger.error('Error generating category schema in loader', {
                        error,
                    });
                    return null;
                }
            }
        )
        .catch((error) => {
            logger.error('Error in category schema promise chain', {
                error,
            });
            return null;
        });

    return {
        category: categoryData,
        searchResultCritical,
        searchResultNonCritical,
        categoryId,
        pageUrl,
        refine: effectiveRefine,
        currency,
        locale,
        initialFiltersOpen,
        categorySchema: categorySchemaPromise,
        seoPagination,
        offset,
        page,
    };
}

export { shouldRevalidate } from '@/lib/revalidation/routes/category';

/**
 * Category page component that displays a product category with filtering, sorting, and pagination.
 * This component uses the createPage factory to handle Suspense patterns.
 * @returns JSX element representing the category page
 */
function ProductGridError() {
    const rawError = useAsyncError();
    const error = rawError instanceof NormalizedApiError ? rawError : null;
    const { t } = useTranslation('common');
    return (
        <div role="alert" className="col-span-full py-8 text-center text-muted-foreground">
            <p>{t('productGrid.loadFailed')}</p>
            {import.meta.env.DEV && error && (
                <div className="mt-2 text-xs font-mono text-muted-foreground/70">
                    {error.status && <span>{error.status} </span>}
                    {error.message && <p>{error.message}</p>}
                </div>
            )}
        </div>
    );
}

/**
 * Component that renders JSON-LD schema when categorySchema promise resolves.
 * Must be inside Suspense boundary to ensure it streams correctly in SSR.
 */
function CategoryJsonLd({
    categorySchemaPromise,
}: {
    categorySchemaPromise: Promise<ReturnType<typeof generateCategorySchema> | null>;
}) {
    const categorySchema = use(categorySchemaPromise);
    const rootData = useRouteLoaderData<typeof rootLoader>('root');
    const nonce = rootData?.nonce ?? undefined;
    return categorySchema ? <JsonLd data={categorySchema} id="category-schema" nonce={nonce} /> : null;
}

// RGBARRY-CUSTOMIZATION: instant-URL — inner component consumes the (possibly deferred) critical
// search + PD zone via use(); the default export wraps it in a Suspense that bridges with the nav logo
// while those stream in on client navigations. See CategoryPage below.
function CategoryPageInner({ loaderData }: { loaderData: CategoryPageData }) {
    const {
        category,
        searchResultNonCritical,
        categoryId,
        pageUrl,
        refine,
        locale,
        currency,
        initialFiltersOpen,
        categorySchema,
        seoPagination,
        offset = 0,
    } = loaderData;
    // RGBARRY-CUSTOMIZATION: instant-URL — critical search + PD zone are resolved objects on document
    // requests (synchronous, no fallback in the SSR HTML) and unresolved promises on client navigations
    // (use() suspends to the CategoryGridSkeleton fallback until they stream in). use() is the one
    // hook that may be called conditionally.
    // RGBARRY-CUSTOMIZATION: a deferred (Promise) critical result means this render came from a client
    // navigation — the PLP mounts fresh after the loading bridge, so we fade the content region in once
    // for a coordinated reveal. On document/SSR requests critical is a resolved object, so no fade runs
    // and products paint at full opacity in the first-paint HTML (LCP/SEO unchanged).
    const isClientNavReveal = loaderData.searchResultCritical instanceof Promise;
    const searchResultCritical =
        loaderData.searchResultCritical instanceof Promise
            ? use(loaderData.searchResultCritical)
            : loaderData.searchResultCritical;
    const page = loaderData.page instanceof Promise ? use(loaderData.page) : (loaderData.page ?? null);

    const config = useConfig();
    const { t } = useTranslation();

    const [filtersOpen, toggleFiltersOpen] = useFiltersPanelState(initialFiltersOpen);
    const limit = config.search.products.hits.limit;
    // RGBARRY-CUSTOMIZATION: instant-URL — `initialCount` is derived here from the resolved critical
    // total (previously loader data) so it is available on both the document and client-nav paths.
    const initialCount = Math.min(limit, Math.max(0, (searchResultCritical.total ?? 0) - offset));

    // RGBARRY-CUSTOMIZATION: reserve skeleton space for EXACTLY the non-critical tiles that stream in
    // on the first page, so the grid's height is correct at first paint and the footer never jumps
    // when the deferred batch arrives (a ~0.16 CLS otherwise). The OOTB `Math.min(8, ...)` cap was
    // sized for `hits.critical: 4` (reserve 4 skeletons); with `hits.critical: 8` it collapsed to
    // `8 - 8 = 0`, reserving nothing for the 16 streamed tiles. Reserve `firstPageCount - criticalCount`
    // instead. Skeletons are cheap placeholder divs, so covering the full first page is the right
    // trade for a stable layout. Wrap in Math.max(0, ...) to stay non-negative.
    const criticalCount = searchResultCritical.hits?.length ?? 0;
    const nonCriticalCount = Math.max(0, initialCount - criticalCount);

    const analytics = useAnalytics();
    const lastTrackedDataRef = useRef<string | null>(null);
    const resultsHeadingRef = useRef<HTMLHeadingElement>(null);

    const location = useLocation();
    const navigation = useNavigation();
    const searchWithoutFiltersParam = useMemo(() => getSearchWithoutFiltersParam(location.search), [location.search]);
    const pageIdentity = `${categoryId}-${currency}-${locale}`;
    const analyticsKey = `${pageIdentity}-${searchWithoutFiltersParam}-${location.hash}`;
    const productGridDataKey = `${pageIdentity}-${searchWithoutFiltersParam}`;
    const selectedFiltersCount = useMemo(
        () => new URLSearchParams(location.search).getAll('refine').length,
        [location.search]
    );

    // QuickFilters "Shop by {label}" header is opt-in per build target via
    // `uiConfig.pages.category.showCategoryLabel`. When on, derive the label from
    // the active `cgid` refinement and pass it down; when off, pass nothing so
    // QuickFilters renders the chips-only baseline. Keeping this in the (shared)
    // route means QuickFilters stays presentational and no vertical has to fork
    // either the component or this route. See @/lib/config.ui.
    const categoryLabel = uiConfig.pages.category.showCategoryLabel
        ? searchResultCritical.refinements?.find((r) => r.attributeId === 'cgid')?.label
        : undefined;
    const isProductGridLoading = useMemo(() => {
        if (navigation.state === 'idle' || !navigation.location) {
            return false;
        }
        const currentRefines = new URLSearchParams(location.search).getAll('refine');
        const nextRefines = new URLSearchParams(navigation.location.search).getAll('refine');
        return (
            currentRefines.length !== nextRefines.length ||
            currentRefines.some((currentRefine, index) => currentRefine !== nextRefines[index])
        );
    }, [location.search, navigation.location, navigation.state]);

    const nonCriticalPromise = useMemo(
        () => searchResultNonCritical.then((r) => r.hits ?? []),
        [searchResultNonCritical]
    );

    // Pagination mode is merchant-configurable: `load-more` (button + infinite scroll, the default)
    // or `traditional` (numbered prev/next that navigates the URL offset). See @/lib/config.ui.
    const paginationConfig = uiConfig.pages.category.pagination;
    const isLoadMoreMode = paginationConfig.mode === 'load-more';

    // "Load more" / infinite scroll: the loader renders the first page (`initialCount` products) and
    // this hook appends further pages via a non-navigating fetch, resetting whenever the underlying
    // query changes (category / sort / refinements). On bfcache-miss back-nav, the hook reads
    // `sfnextLoaded` from history.state and auto-fetches to restore the prior scroll depth.
    const {
        appended,
        loadedCount,
        total: loadMoreTotal,
        hasMore,
        capReached,
        isLoading: isLoadingMore,
        isRestoring,
        restorationTarget,
        hasError: loadMoreError,
        firstNewIndex,
        loadMore,
    } = useLoadMoreProducts({
        refine,
        sort: searchResultCritical.selectedSortingOption,
        currency,
        initialCount,
        criticalCount,
        nonCriticalHits: nonCriticalPromise,
        total: searchResultCritical.total,
        batchSize: paginationConfig.batchSize,
        mobileBatchSize: paginationConfig.mobileBatchSize,
        maxProducts: paginationConfig.maxProducts,
        identity: productGridDataKey,
        offset,
    });

    // Accessibility: after a "load more" appends a batch, move focus to its first tile so keyboard and
    // screen-reader users land on the new content instead of staying on the (now mid-page) button.
    const firstNewItemRef = useRef<HTMLDivElement>(null);
    const focusTargetKey = firstNewIndex === null ? null : `${appended.length}-${firstNewIndex}`;
    useEffect(() => {
        if (focusTargetKey === null || !firstNewItemRef.current) {
            return;
        }
        const node = firstNewItemRef.current;
        // ProductTile's root isn't natively focusable; make it programmatically focusable, focus it,
        // then drop the tabindex on blur so it doesn't linger in the tab order.
        node.setAttribute('tabindex', '-1');
        node.focus({ preventScroll: true });
        const onBlur = () => node.removeAttribute('tabindex');
        node.addEventListener('blur', onBlur, { once: true });
        return () => node.removeEventListener('blur', onBlur);
    }, [focusTargetKey]);

    // Persist loaded count in sessionStorage keyed by React Router's history key. React Router
    // overwrites history.state with { key } on navigation, so history.state is not safe for custom
    // data. sessionStorage survives SPA navigations and is the same mechanism React Router uses
    // for its own scroll positions. URL stays clean — no params exposed to users.
    const STORAGE_KEY = 'sfnext:loadMore';
    useEffect(() => {
        if (!isLoadMoreMode || typeof window === 'undefined' || isRestoring) {
            return;
        }
        const historyKey = (window.history.state as { key?: string } | null)?.key;
        if (!historyKey) return;
        try {
            if (loadedCount > initialCount) {
                const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
                stored[historyKey] = loadedCount;
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
            } else {
                const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
                delete stored[historyKey];
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
            }
        } catch {
            // sessionStorage full or unavailable — non-critical, skip
        }
    }, [isLoadMoreMode, isRestoring, loadedCount, initialCount]);

    // RGBARRY-CUSTOMIZATION: explicit scroll restoration for the "Load more" PLP.
    //
    // React Router's <ScrollRestoration> performs a single `useLayoutEffect` scroll on the
    // destination route's first commit. On this route that first commit is short — the product
    // tiles stream in via deferred loaders and the back-nav catch-up fetch resolves asynchronously
    // — so the one-shot restore lands against a document that isn't tall enough yet and then never
    // re-applies as content grows. The net effect the shopper sees: clicking Back from a PDP lands
    // them at the top (or, once the grid finishes growing, near the footer) rather than where they
    // left off. We therefore persist `scrollY` keyed by RR's history key and re-apply it with a
    // bounded rAF loop that waits until the document is tall enough (or a timeout elapses), while
    // aborting on any deliberate user scroll input so we never fight a shopper who scrolls first.
    const SCROLL_STORAGE_KEY = 'sfnext:plpScroll';
    // True only while the rAF restore loop is actively driving the scroll position, so the persist
    // listener below ignores the intermediate positions our own `scrollTo` calls generate.
    const restoringScrollRef = useRef(false);
    // True once a navigation is in flight. When the shopper clicks a tile, React Router sets
    // `navigation.state` to 'loading' and (before the destination commits) the window scrolls back
    // to the top while `history.state.key` still points at THIS page. Without this guard the persist
    // listener would write that transient `scrollY: 0` over the real saved position for our key,
    // and the back-nav restore would then have nothing to restore to. Freezing persistence the
    // moment a navigation begins preserves the last real position the shopper actually saw.
    const isNavigatingRef = useRef(false);
    useEffect(() => {
        isNavigatingRef.current = navigation.state !== 'idle';
    }, [navigation.state]);

    // Persist scrollY (throttled to one write per animation frame) keyed by RR's history key.
    useEffect(() => {
        if (!isLoadMoreMode || typeof window === 'undefined') {
            return;
        }
        let frame = 0;
        const persist = () => {
            frame = 0;
            if (restoringScrollRef.current || isNavigatingRef.current) {
                return;
            }
            const historyKey = (window.history.state as { key?: string } | null)?.key;
            if (!historyKey) return;
            try {
                const stored = JSON.parse(sessionStorage.getItem(SCROLL_STORAGE_KEY) || '{}');
                stored[historyKey] = Math.round(window.scrollY);
                sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(stored));
            } catch {
                // sessionStorage full or unavailable — non-critical, skip
            }
        };
        const onScroll = () => {
            if (frame) return;
            frame = window.requestAnimationFrame(persist);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, [isLoadMoreMode]);

    // Re-apply the saved scrollY on back/forward (POP) navigation once the document is tall enough.
    const navigationType = useNavigationType();
    const scrollRestoredRef = useRef(false);
    useEffect(() => {
        if (!isLoadMoreMode || typeof window === 'undefined') {
            return;
        }
        // Only restore on a genuine history POP, and only once per mount.
        if (navigationType !== NavigationType.Pop || scrollRestoredRef.current) {
            return;
        }
        const historyKey = (window.history.state as { key?: string } | null)?.key;
        if (!historyKey) return;
        let savedY = 0;
        try {
            const stored = JSON.parse(sessionStorage.getItem(SCROLL_STORAGE_KEY) || '{}');
            savedY = stored[historyKey] ?? 0;
        } catch {
            return;
        }
        if (savedY <= 0) return;
        scrollRestoredRef.current = true;
        restoringScrollRef.current = true;

        // The restore has to survive a moving target: on a load-more back-nav the document height
        // changes several times before it settles — the height-reserving skeleton grid mounts (tall),
        // then swaps to the real tiles (briefly short: the critical batch renders before the catch-up
        // fetch's appended tiles arrive), then grows again as those tiles fill in. Each transient
        // shrink lets the browser clamp our restored scroll back toward the top. So rather than
        // scrolling once, we re-apply every frame and only stop once the document height has held
        // steady (and is tall enough to reach savedY) for STABLE_MS, or we hit TIMEOUT_MS. Any
        // deliberate user scroll gesture aborts immediately so we never fight a shopper who scrolls.
        // Generous cap: the loop normally exits ~STABLE_MS after content settles (fast in production).
        // The cap only bounds the pathological slow-network case where the catch-up fetch is still in
        // flight — landing imperfectly there is acceptable; leaving the shopper at the top is not.
        const TIMEOUT_MS = 5000;
        const STABLE_MS = 350;
        let raf = 0;
        let cancelled = false;
        let lastHeight = -1;
        let stableSince = 0;
        const start = performance.now();
        const abort = () => {
            cancelled = true;
            restoringScrollRef.current = false;
        };
        // Listen for wheel/touchmove/keydown rather than 'scroll' because our own `scrollTo` calls
        // fire 'scroll' but not these — so we abort on real user intent, not our own corrections.
        window.addEventListener('wheel', abort, { passive: true, once: true });
        window.addEventListener('touchmove', abort, { passive: true, once: true });
        window.addEventListener('keydown', abort, { once: true });

        const tick = (now: number) => {
            if (cancelled) return;
            const height = document.documentElement.scrollHeight;
            const maxScroll = height - window.innerHeight;
            window.scrollTo(0, Math.min(savedY, maxScroll));
            // The height must be unchanged AND tall enough to reach savedY to count as "settled".
            if (height === lastHeight && maxScroll >= savedY) {
                if (stableSince === 0) stableSince = now;
            } else {
                stableSince = 0;
                lastHeight = height;
            }
            const settled = stableSince !== 0 && now - stableSince >= STABLE_MS;
            if (!settled && now - start < TIMEOUT_MS) {
                raf = window.requestAnimationFrame(tick);
            } else {
                restoringScrollRef.current = false;
            }
        };
        raf = window.requestAnimationFrame(tick);
        return () => {
            cancelled = true;
            restoringScrollRef.current = false;
            // Reset so a remount re-runs the restore. React StrictMode (dev) double-invokes effects
            // as mount → cleanup → remount; without this the first mount would latch the guard, the
            // cleanup would cancel the pending rAF, and the remount would bail — restoring nothing.
            scrollRestoredRef.current = false;
            if (raf) window.cancelAnimationFrame(raf);
            window.removeEventListener('wheel', abort);
            window.removeEventListener('touchmove', abort);
            window.removeEventListener('keydown', abort);
        };
    }, [isLoadMoreMode, navigationType]);

    const [, startTransition] = useTransition();
    // Compare on the meaningful search only. Opening/closing the filters panel writes a client-only
    // `filters` param (see use-filters-panel-state); that toggle must not steal focus up to the
    // heading — the panel manages its own focus. (W-23325653)
    const lastSearchParamsRef = useRef<string>(getSearchWithoutClientOnlyParams(location.search));

    useEffect(() => {
        // Move focus to results heading after refinement or sort changes
        const meaningfulSearch = getSearchWithoutClientOnlyParams(location.search);
        if (navigation.state === 'idle' && lastSearchParamsRef.current !== meaningfulSearch) {
            lastSearchParamsRef.current = meaningfulSearch;
            // Allow the DOM to update before moving focus
            requestAnimationFrame(() => {
                resultsHeadingRef.current?.focus();
            });
        }
    }, [navigation.state, location.search]);

    // When the shopper opens the filters panel, move focus into it so keyboard users land on the
    // refinements instead of tabbing forward from a toggle that sits after the panel in the DOM.
    // Only fire on the closed -> open transition, never on initial render. (W-23325653)
    const refinementsPanelRef = useRef<HTMLDivElement>(null);
    const prevFiltersOpenRef = useRef(filtersOpen);
    useEffect(() => {
        if (filtersOpen && !prevFiltersOpenRef.current) {
            requestAnimationFrame(() => {
                // RGBARRY-CUSTOMIZATION: preventScroll stops the browser from scroll-jumping the page
                // to the newly-focused panel when the drawer opens (removes the open-time jank).
                refinementsPanelRef.current?.focus({ preventScroll: true });
            });
        }
        prevFiltersOpenRef.current = filtersOpen;
    }, [filtersOpen]);

    useEffect(() => {
        // Only track if we haven't already tracked this specific data combination
        if (analyticsKey !== lastTrackedDataRef.current) {
            lastTrackedDataRef.current = analyticsKey;

            startTransition(() => {
                void nonCriticalPromise
                    .then((searchHitsData: ShopperSearch.schemas['ProductSearchHit'][]) => {
                        if (analytics) {
                            void analytics.trackViewCategory({
                                category,
                                searchResults: [...(searchResultCritical.hits ?? []), ...searchHitsData],
                                sort:
                                    searchResultCritical.selectedSortingOption ||
                                    searchResultCritical.sortingOptions?.[0]?.label ||
                                    '',
                                refinements: searchResultCritical.selectedRefinements ?? {},
                                offset: searchResultCritical.offset,
                                limit,
                                total: searchResultCritical.total,
                            });
                        }
                    })
                    .catch(() => {
                        // Silently handle promise rejection
                    });
            });
        }
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [analytics, category, analyticsKey, nonCriticalPromise]);

    const handleProductClick = useCallback(
        (product: ShopperSearch.schemas['ProductSearchHit']) => {
            if (analytics) {
                void analytics.trackClickProductInCategory({
                    category,
                    product,
                });
            }
        },
        [analytics, category]
    );

    return (
        <>
            <SeoMeta
                title={category.name || category.id}
                description={category.pageDescription || category.description}
                openGraph={{
                    type: 'website',
                    url: pageUrl,
                }}
            />
            {/* SEO pagination hints: let crawlers walk the full result set via `?page=N` even though
                shoppers use the JS "load more" flow. React 19 hoists these <link>s into <head>. */}
            {seoPagination?.prevUrl && <link rel="prev" href={seoPagination.prevUrl} />}
            {seoPagination?.nextUrl && <link rel="next" href={seoPagination.nextUrl} />}
            {/* RGBARRY-CUSTOMIZATION: on client navigations the content region fades in once as the
                loading bridge hands off, for a coordinated reveal instead of a hard swap. Gated to
                client navs (isClientNavReveal) so the SSR/document paint stays instant (no LCP hit). */}
            <div
                className={`pb-16 -mt-8${isClientNavReveal ? ' motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300' : ''}`}>
                {/* RGBARRY-CUSTOMIZATION: plpTopFullWidth — full-width PD zone below the header,
                    above the breadcrumb. Omitting fallbackElement/errorElement is intentional: this
                    region is optional editorial content, so an empty/unconfigured PD page should
                    render nothing rather than a hardcoded fallback (see docs/README-PAGE-DESIGNER.md
                    "Error Fallbacks: Anti-Pattern"). */}
                <Region page={page} regionId="plpTopFullWidth" />

                <div className="section-container pt-8">
                    <div className="mb-4">
                        <CategoryBreadcrumbs category={category} />
                    </div>

                    <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        {/* RGBARRY-CUSTOMIZATION: heading shows a subdued item count instead of "(N)" */}
                        <h1
                            ref={resultsHeadingRef}
                            tabIndex={-1}
                            className="text-3xl font-bold leading-none tracking-[-0.75px] text-card-foreground flex items-baseline gap-3 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                            <span>{category?.name || category.id}</span>
                            <span className="text-sm font-normal tracking-normal text-muted-foreground">
                                {searchResultCritical.total} {searchResultCritical.total === 1 ? 'item' : 'items'}
                            </span>
                        </h1>
                        <UITarget targetId="sfcc.plp.search.summary" />
                    </div>

                    {/* RGBARRY-CUSTOMIZATION: filter/sort bar lifted to full width above the drawer + grid
                        row so the Filters button stays anchored when the panel opens — the drawer slides
                        out beneath the button and only the grid shrinks; the button no longer shifts. */}
                    {/* Filters toggle button + Quick Filters - mobile only (above panel) */}
                    <div className="lg:hidden mb-4 flex flex-col items-start gap-2" data-slot="filters-wrapper">
                        <div className="flex w-full items-center justify-between gap-2">
                            <FiltersButton
                                onClick={toggleFiltersOpen}
                                isActive={filtersOpen}
                                selectedFiltersCount={selectedFiltersCount}
                            />
                            {searchResultCritical?.sortingOptions && searchResultCritical.sortingOptions.length > 0 && (
                                <CategorySorting result={searchResultCritical} />
                            )}
                        </div>
                        <QuickFilters category={category} categoryLabel={categoryLabel} />
                    </div>

                    {/* Filters toggle button + Quick Filters - desktop only. Sort by shares the row (right-aligned). */}
                    <div className="mb-4 hidden lg:flex lg:items-center lg:gap-4" data-slot="filters-wrapper">
                        <FiltersButton
                            onClick={toggleFiltersOpen}
                            isActive={filtersOpen}
                            selectedFiltersCount={selectedFiltersCount}
                        />
                        <QuickFilters category={category} categoryLabel={categoryLabel} />
                        {searchResultCritical?.sortingOptions && searchResultCritical.sortingOptions.length > 0 && (
                            <div className="ml-auto flex-shrink-0">
                                <CategorySorting result={searchResultCritical} />
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col lg:flex-row gap-2">
                        {/* Category Refinements - slides out beneath the Filters button on the left; grid shrinks */}
                        {filtersOpen && (
                            <div
                                ref={refinementsPanelRef}
                                tabIndex={-1}
                                role="region"
                                aria-label={t('categoryRefinements:filtersButtonLabel')}
                                className="df-filter-drawer w-full lg:w-64 lg:flex-shrink-0 outline-none">
                                <CategoryRefinements result={searchResultCritical} refine={refine} />
                            </div>
                        )}

                        <div className="flex-grow">
                            <ActiveFilters result={searchResultCritical} />

                            <UITarget targetId="sfcc.plp.agent.categoryHelper" />
                            <UITarget targetId="sfcc.plp.search.results">
                                {isRestoring ? (
                                    <div
                                        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-8"
                                        aria-busy="true">
                                        {Array.from({ length: restorationTarget }, (_, i) => (
                                            <ProductTileSkeleton key={`restore-skeleton-${i}`} />
                                        ))}
                                    </div>
                                ) : (
                                    <DeferredProductGrid
                                        key={productGridDataKey}
                                        critical={searchResultCritical.hits ?? []}
                                        nonCritical={nonCriticalPromise}
                                        appended={isLoadMoreMode ? appended : undefined}
                                        firstNewIndex={isLoadMoreMode ? firstNewIndex : undefined}
                                        firstNewItemRef={isLoadMoreMode ? firstNewItemRef : undefined}
                                        appendPending={isLoadMoreMode ? isLoadingMore : undefined}
                                        nonCriticalCount={nonCriticalCount}
                                        hasRefinementsPanel={filtersOpen}
                                        isLoading={isProductGridLoading}
                                        handleProductClick={handleProductClick}
                                        topCategoryName={
                                            category.parentCategoryTree?.find((p) => p.id !== 'root')?.name ??
                                            category.name
                                        }
                                        errorElement={<ProductGridError />}
                                    />
                                )}
                            </UITarget>

                            {!isProductGridLoading &&
                                (!isRestoring || loadMoreError) &&
                                (isLoadMoreMode ? (
                                    <div className="mt-10">
                                        <LoadMore
                                            loadedCount={loadedCount}
                                            total={loadMoreTotal}
                                            hasMore={hasMore}
                                            capReached={capReached}
                                            isLoading={isLoadingMore}
                                            hasError={loadMoreError}
                                            onLoadMore={loadMore}
                                        />
                                    </div>
                                ) : (
                                    searchResultCritical.total > 1 && (
                                        <div className="mt-10">
                                            <CategoryPagination
                                                limit={limit}
                                                offset={searchResultCritical.offset}
                                                total={searchResultCritical.total}
                                            />
                                        </div>
                                    )
                                ))}
                        </div>
                    </div>
                </div>
            </div>
            <Suspense fallback={null}>
                <CategoryJsonLd categorySchemaPromise={categorySchema} />
            </Suspense>
        </>
    );
}

// RGBARRY-CUSTOMIZATION: instant-URL — on client navigations the loader defers the critical search and
// PD zone (see loader), so it returns immediately and React Router commits the new URL on click. This
// Suspense bridges the gap with a PLP skeleton (breadcrumb + heading + tile grid) until those deferred
// promises stream in and CategoryPageInner's use() calls resolve — click feedback comes from the app
// shell's top progress bar (see _app.tsx). On document/SSR requests both are already resolved, so use()
// is synchronous and the fallback never appears in the HTML — SEO/LCP are unaffected.
export default function CategoryPage({ loaderData }: { loaderData: CategoryPageData }) {
    return (
        <Suspense
            fallback={
                // RGBARRY-CUSTOMIZATION: match the resolved PLP wrapper by cancelling the app shell's `main.pt-8`.
                // CategoryGridSkeleton stays parent-layout-neutral because the fixed nav overlay also reuses it.
                <div className="-mt-8">
                    <CategoryGridSkeleton />
                </div>
            }>
            <CategoryPageInner loaderData={loaderData} />
        </Suspense>
    );
}
